import { useState, useEffect, useCallback } from 'react';

interface SwipeHandlers {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeUp?: () => void;
  onSwipeDown?: () => void;
}

interface SwipeGestureOptions {
  threshold?: number;
  preventDefaultTouchend?: boolean;
}

export const useSwipeGesture = (
  elementRef: React.RefObject<HTMLElement>,
  handlers: SwipeHandlers,
  options: SwipeGestureOptions = {}
) => {
  const { threshold = 50, preventDefaultTouchend = true } = options;
  
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);

  // Set up the touch start handler
  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      setTouchEnd(null);
      setTouchStart({
        x: e.targetTouches[0].clientX,
        y: e.targetTouches[0].clientY
      });
    },
    []
  );

  // Set up the touch move handler
  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      setTouchEnd({
        x: e.targetTouches[0].clientX,
        y: e.targetTouches[0].clientY
      });
    },
    []
  );

  // Set up the touch end handler
  const handleTouchEnd = useCallback(
    (e: TouchEvent) => {
      if (!touchStart || !touchEnd) return;

      const distanceX = touchStart.x - touchEnd.x;
      const distanceY = touchStart.y - touchEnd.y;
      const isHorizontalSwipe = Math.abs(distanceX) > Math.abs(distanceY);
      
      // Detect swipe direction if it exceeds the threshold
      if (isHorizontalSwipe) {
        if (distanceX > threshold && handlers.onSwipeLeft) {
          handlers.onSwipeLeft();
          if (preventDefaultTouchend) e.preventDefault();
        } else if (distanceX < -threshold && handlers.onSwipeRight) {
          handlers.onSwipeRight();
          if (preventDefaultTouchend) e.preventDefault();
        }
      } else {
        if (distanceY > threshold && handlers.onSwipeUp) {
          handlers.onSwipeUp();
          if (preventDefaultTouchend) e.preventDefault();
        } else if (distanceY < -threshold && handlers.onSwipeDown) {
          handlers.onSwipeDown();
          if (preventDefaultTouchend) e.preventDefault();
        }
      }

      // Reset touch positions
      setTouchEnd(null);
      setTouchStart(null);
    },
    [touchStart, touchEnd, handlers, threshold, preventDefaultTouchend]
  );

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    // Add event listeners
    element.addEventListener('touchstart', handleTouchStart);
    element.addEventListener('touchmove', handleTouchMove);
    element.addEventListener('touchend', handleTouchEnd);

    // Clean up event listeners
    return () => {
      element.removeEventListener('touchstart', handleTouchStart);
      element.removeEventListener('touchmove', handleTouchMove);
      element.removeEventListener('touchend', handleTouchEnd);
    };
  }, [elementRef, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return {
    touchStart,
    touchEnd
  };
};

export default useSwipeGesture; 